import * as d3 from "d3";
import { CrainData } from "./shared/CrainD3Vis.js";
import {
  BarChartControl,
  ColumnChartControl,
  GeneralChartControl,
  OneDHeatMapControl,
  TableControl
} from "./shared/GeneralChartControl.js";
import { LineChartControl } from "./shared/LineChartControl.js";
import { ScatterPlotControl } from "./shared/ScatterPlotControl.js";
import { getUpdatedMetrics, getUpdatedZ } from "./shared/utilities";
import { WorldMapControl } from "./shared/WorldMapControl.js";
import store from '@/store/store'

export default class {
  svg;
  type;
  control;
  color = "recency";
  metric = "size";
  sortX = "size_des";
  sortY = "size_des";
  sortZ = 0;
  normalizeOverX = false;
  sizeLimit = null;
  sizeRange = null;
  recencyLimit = null;
  recencyRange = null;
  queryUpdated = false;
  dataLimit = false;
  colorOptions = [
    { value: "size", text: "Size" },
    { value: "recency", text: "Recency" }
  ];
  metricOptions = [
    { value: "size", text: "Size" },
    { value: "recency", text: "Recency" }
  ];
  sortXYOptions = [
    { value: "normal", text: "Normal" },
    { value: "size", text: "Size" },
    { value: "filtsize", text: "Size (Filter)" },
    { value: "recency", text: "Recency" },
    { value: "filtrecency", text: "Recency (Filter)" },
    { value: "clustering", text: "Clustering" },
    { value: "clustering_docs", text: "Clustering (documents)" }
  ];

  constructor(svg, type, settings=null) {
    if (
      ![
        "barchart",
        "columnchart",
        "heatmap",
        "linechart",
        "onedheatmap",
        "scatterplot",
        "worldmap",
        "table"
      ].includes(type)
    ) {
      console.error("Invalid type:", type);
    }
    if(settings){
      this.color = settings.color;
      this.metric = settings.metric;
      this.sortX = settings.sortX;
      this.sortY = settings.sortY;
    }
    this.svg = svg;
    this.type = type;
  }
  setQuery(value, redraw=true) {
    this.query = JSON.stringify(value);
  }
  getSeparatedColorOptions() {
    const generalOptions = [];
    const filterOptions = [];

    this.colorOptions.forEach(o => {
      if (o.text.includes("filter")) {
        filterOptions.push(o);
      } else {
        generalOptions.push(o);
      }
    });

    if (filterOptions.length === 0) {
      return generalOptions;
    }

    return [
      { text: "General", value: null, disabled: true },
      ...generalOptions,
      { text: "Filter", value: null, disabled: true },
      ...filterOptions
    ];
  }
  setColor(value, redraw=true) {
    if (
      ![
        "size",
        "recency",
        "origsize",
        "linked",
        "filtrecency",
        "relrecency",
        "relsize",
        "filtsize"
      ].includes(value)
    ) {
      console.error("Invalid value:", value);
    }
    this.color = value;

    console.log("setting color");
    if(redraw){
     console.log("Redrawing");
      this.draw(true);
    }
  }
  getSeparatedMetricOptions() {
    const generalOptions = [];
    const filterOptions = [];

    this.metricOptions.forEach(o => {
      if (o.text.includes("filter")) {
        filterOptions.push(o);
      } else {
        generalOptions.push(o);
      }
    });

    if (filterOptions.length === 0) {
      return generalOptions;
    }

    return [
      { text: "General", value: null, disabled: true },
      ...generalOptions,
      { text: "Filter", value: null, disabled: true },
      ...filterOptions
    ];
  }
  setMetric(value, redraw=true) {
    if (
      ![
        "size",
        "recency",
        "origsize",
        "linked",
        "filtrecency",
        "relrecency",
        "relsize",
        "filtsize"
      ].includes(value)
    ) {
      console.error("Invalid value:", value);
    }

    this.metric = value;
    console.log("setting metric");
    if(redraw){
     console.log("Redrawing");
      this.draw(true);
    }
  }
  setSortX(value, redraw=true) {
    if (
      ![
        "normal",
        "normal_des",
        "size",
        "size_des",
        "filtsize",
        "filtsize_des",
        "recency",
        "recency_des",
        "filtrecency",
        "filtrecency_des",
        "clustering",
        "clustering_docs"
      ].includes(value)
    ) {
      console.error("Invalid value:", value);
    }

    this.sortX = value;

    console.log("setting sortX ", this.sortX);
    if(redraw){
     console.log("Redrawing");
      this.draw(true);
    }
  }
  setSortY(value, redraw=true) {
    if (
      ![
        "normal",
        "normal_des",
        "size",
        "filtsize",
        "size_des",
        "filtsize_des",
        "recency",
        "filtrecency",
        "recency_des",
        "filtrecency_des",
        "clustering",
        "clustering_docs"
      ].includes(value)
    ) {
      console.error("Invalid value:", value);
    }

    this.sortY = value;

    console.log("setting sortY", this.sortY);
    if(redraw){
     console.log("Redrawing");
      this.draw(true);
    }
  }
  getSortZOptions() {
    if (!this.control) {
      return [];
    }

    return getUpdatedZ(this.control.crainData.rawData.layers[2]);
  }
  setSortZ(value, redraw=true) {
    this.sortZ = value;

    console.log("setting sortZ");
    if(redraw){
     console.log("Redrawing");
      this.draw(true);
    }
  }
  setNormalizeOverX(value, redraw=true) {
    if (typeof value !== "boolean") {
      console.error("Value should be boolean:", value);
    }

    this.normalizeOverX = value;

    console.log("setting normalize");
    if(redraw){
      this.draw();
    }
  }
  setRecencyLimit(value, redraw=true) {
    this.recencyLimit = value;

    console.log("setting recency limit");
    if(redraw){
     console.log("Redrawing");
      this.draw(true);
    }
  }
  setSizeLimit(value, redraw=true) {
    this.sizeLimit = value;

    console.log("setting size limit");
    if(redraw){
      console.log("Redrawing");
      this.draw(true);
    }
  }
  setDataLimit(value, redraw=true) {
    this.dataLimit = value;
    
    console.log("setting data limit");
    if(redraw){
      console.log("Redrawing");
      let tempQuery = JSON.parse(this.query);
      tempQuery.data_limits = { enabled: value, limit: 100 };
      this.query = JSON.stringify(tempQuery);
      this.queryUpdated = true;
      this.draw(true);
    }
  }

  

  async init(cData, cType = this.type) {
    //document.getElementById("load-div-"+this.type).classList.add("show");
    return new Promise((resolve, reject) => {
      this.type = cType;

      if (!cData || this.queryUpdated) {
        this.queryUpdated = false;
        const svgElement = d3.select(this.svg);
        const crainData = new CrainData(this.query);
        return crainData.load(this.dataLimit).then(() => {
          switch (this.type) {
            case "barchart":
              this.control = new BarChartControl(
                svgElement, 
                crainData, 
                this.svg,
                this.dataLimit
              );
              resolve();
              break;
            case "columnchart":
              this.control = new ColumnChartControl(
                svgElement,
                crainData,
                this.svg
              );
              resolve();
              break;
            case "heatmap":
              this.control = new GeneralChartControl(
                svgElement,
                crainData,
                this.svg
              );
              resolve();
              break;
            case "linechart":
              this.control = new LineChartControl(
                svgElement,
                crainData,
                this.svg
              );
              resolve();
              break;
            case "onedheatmap":
              this.control = new OneDHeatMapControl(
                svgElement,
                crainData,
                this.svg
              );
              resolve();
              break;
            case "scatterplot":
              this.control = new ScatterPlotControl(
                svgElement,
                crainData,
                this.svg
              );
              resolve();
              break;
            case "worldmap":
              this.control = new WorldMapControl(svgElement, crainData, this.svg);
              resolve();
              break;
            case "table":
              this.control = new TableControl(svgElement, crainData, this.svg);
              resolve();
              break;
          }
        })
        .catch(error => {
          const svg = d3.select(this.svg);
          svg.selectAll('g#main').remove();
            //const svg = d3.select("#mySvg");
            // Append text element to the SVG
          svg.append('g').attr('id', 'main');
          svg.select('g#main')
            .append("text")
            .attr("x", svg.attr("width") / 2)
            .attr("y", svg.attr("height") / 2)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text("No Enough Data to draw :(");
        });
      }

      const svgElement = d3.select(cData.svgDiv);
      const crainData = cData.crainData;

      switch (cType) {
        case "barchart":
          this.control = new BarChartControl(svgElement, crainData, cData.svgDiv, this.dataLimit);
          resolve();
          break;
        case "columnchart":
          this.control = new ColumnChartControl(
            svgElement,
            crainData,
            cData.svgDiv
          );
          resolve();
          break;
        case "heatmap":
          this.control = new GeneralChartControl(
            svgElement,
            crainData,
            cData.svgDiv
          );
          resolve();
          break;
        case "linechart":
          this.control = new LineChartControl(
            svgElement,
            crainData,
            cData.svgDiv
          );
          resolve();
          break;
        case "onedheatmap":
          this.control = new OneDHeatMapControl(
            svgElement,
            crainData,
            cData.svgDiv
          );
          resolve();
          break;
        case "scatterplot":
          this.control = new ScatterPlotControl(
            svgElement,
            crainData,
            cData.svgDiv
          );
          resolve();
          break;
        case "worldmap":
          this.control = new WorldMapControl(svgElement, crainData, cData.svgDiv);
          resolve();
          break;
      }
    })
    
  }
  // TODO:
  // AnalysisViews aren't using these callbacks anymore.
  // When they are removed from FocusView visualizations as well, they can also be deleted here
  async draw(
    reset = false,
    //resetCallback,
    //metricCallback,
    //rangeCallback,
    cType,
    inputs
  ) {

    //store.dispatch('loadAnimStart');
    store.dispatch('saveLoadingStep', "Drawing " + (cType||this.type));

    let cData = this.control !== undefined ? this.control : null;
    if (reset) {
      await this.init(cData, cType);
    }

    // This check is mainly to prevent console errors when an automatic refresh is triggered in local development
    if (!this.control || !this.control.config) {
      //console.log(this.control, this.control.config);
      console.warn("No config");
      return;
    }
    if(inputs){
      console.log(reset)
      console.log("updating existing chart " + (cType||this.type))
    }
    else{
      console.log(reset, this.sortX, this.sortY);
      console.log("drawing chart " + (cType||this.type))
    }
    this.control.config.color = this.color;
    this.control.config.normOverX = this.normalizeOverX;
    this.control.config.metric = this.metric;
    /*
    // TODO: Remove this when callbacks removed
    if (reset === true && resetCallback) {
      if (typeof resetCallback === "function") {
        resetCallback(getUpdatedZ(this.control.crainData.rawData.layers[2]));
      } else if (typeof resetCallback !== "function") {
        console.error("resetCallback should be function", resetCallback);
      }
    }

    // TODO: Remove this when callbacks removed
    if (reset === true && rangeCallback) {
      if (typeof rangeCallback === "function") {
        rangeCallback(
          this.control.crainData.getSizeRange(),
          this.control.crainData.getRecencyRange(),
          true
        );
      } else if (typeof rangeCallback !== "function") {
        console.error("rangeCallback should be function", rangeCallback);
      }
    }

    // TODO: Remove this when callbacks removed
    if (reset === true && metricCallback) {
      if (typeof metricCallback === "function") {
        metricCallback(getUpdatedMetrics(this.control.crainData.processedData));
      } else if (typeof metricCallback !== "function") {
        console.error("metricCallback should be function", metricCallback);
      }
    }
    */
   
    this.sizeRange = this.control.crainData.getSizeRange();
    if (!this.sizeLimit) {
      this.sizeLimit = this.sizeRange;
    }

    this.recencyRange = this.control.crainData.getRecencyRange();
    if (!this.recencyLimit) {
      this.recencyLimit = this.recencyRange;
    }

    const updatedMetrics = getUpdatedMetrics(
      this.control.crainData.processedData
    );
    if (updatedMetrics.length > 0) {
      this.colorOptions = updatedMetrics;
      this.metricOptions = updatedMetrics;
    }

    this.control.crainData.prep(
      this.sortZ,
      0,
      this.sortX,
      this.sortY,
      this.sizeLimit,
      this.recencyLimit
    );
    this.control.update(reset, this.type);
  }

  save(type) {
    this.control.save(type);
  }

  destroy() {
    if (this.control) {
      this.control.destroy();
    }
  }
}
